// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';
import { SyntheticEvent } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import Link from 'presentations/views/components/atoms/Link';
import Icon from 'presentations/views/components/atoms/Icon';
import Image from 'presentations/views/components/atoms/Image';
import IconButton from 'presentations/views/components/atoms/IconButton';
import {
  spacing,
  fontSize,
  fontWeight,
  mediaMax,
  borderRadius,
  isMediaMax,
} from 'presentations/views/styles';
import { black, white, gray01, gray04 } from 'presentations/views/styles/color';
import { Role } from 'domains/models/team_user/role';
import Skeleton from 'presentations/views/components/atoms/Skeleton';
import { ServiceDetailListItem } from 'presentations/api/schema/service';
import { formatTaxExcludedPrice } from 'presentations/views/helpers/formatter';
import { dashboardPath, serviceListPath } from 'presentations/routes/path';

export interface Props {
  teamUid: string;
  service: ServiceDetailListItem;
  role: Role;
  onClickMenu?: any;
  onClickShowEventTypes?: any;
  isLoading?: boolean;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const service = props.service;
  const isMobile = isMediaMax('tablet');

  return (
    <Card data-intercom-target="event-type-list-item">
      <Content>
        <Header>
          {service.coverImage && (
            <CoverImageContainer>
              <CoverImage
                src={service.coverImage}
                alt={service.name}
                width={isMobile ? 120 : 60}
                height={isMobile ? 120 : 60}
                fit={'crop'}
              />
            </CoverImageContainer>
          )}
          <Block>
            <ServiceTitle>{service.name}</ServiceTitle>
            {service.description && (
              <ServiceDesc>{service.description}</ServiceDesc>
            )}
            {service.price && (
              <ServicePrice>
                {formatTaxExcludedPrice(service)}{' '}
                {service.isPaid && service.taxRate
                  ? `（${t(
                      'taxInclusive_' + String(service.taxRate.taxInclusive),
                    )}）`
                  : ''}
              </ServicePrice>
            )}
            <EventTypeLinkContainer>
              {service && service.eventTypes.length > 0 ? (
                <EventTypeLink
                  onClick={(e: SyntheticEvent) => {
                    props.onClickShowEventTypes(service);
                  }}
                >
                  <AllowIcon name="chevron-right" color={'primary'} />
                  <EventTypeCount>
                    {t('Setting Count', { num: service.eventTypes.length })}
                  </EventTypeCount>
                </EventTypeLink>
              ) : (
                <EventTypeLink href={dashboardPath()}>
                  <AllowIcon name="chevron-right" color={'primary'} />
                  <EventTypeCount>{t('No Setting')}</EventTypeCount>
                </EventTypeLink>
              )}
            </EventTypeLinkContainer>
          </Block>
        </Header>
      </Content>
      <MenuContainer data-intercom-target="event-type-list-item-menu">
        {props.role <= Role.Admin && (
          <IconButton
            onClick={(e: SyntheticEvent) => props.onClickMenu(e, service)}
          >
            <SettingIcon name="settings" />
          </IconButton>
        )}
      </MenuContainer>
    </Card>
  );
}

export function LoadingComponent() {
  return (
    <Card>
      <Content>
        <Header>
          <Block>
            <TagList>
              <Skeleton width="100px" variant="text" />
            </TagList>
            <ServiceTitle>
              <Skeleton width="100%" variant="text" />
            </ServiceTitle>
          </Block>
        </Header>
      </Content>
    </Card>
  );
}

const Card = styled.div`
  display: flex;
  position: relative;
  background: ${white};
  padding: ${spacing(32)};
  border: 1px solid ${gray04};
  border-radius: ${borderRadius(16)};
  ${mediaMax.desktop} {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: ${spacing(24)};
  }
`;
const CoverImageContainer = styled.div`
  margin-right: ${spacing(24)};
  ${mediaMax.tablet} {
    margin-right: ${spacing(16)};
  }
`;
const CoverImage = styled(Image)`
  width: 60px;
  height: 60px;
  object-fit: cover;
  ${mediaMax.tablet} {
    width: 120px;
    height: 120px;
  }
`;
const Content = styled.div`
  width: 100%;
`;
const Header = styled.div`
  display: flex;
  width: 100%;
  color: ${gray01};
  ${mediaMax.tablet} {
    margin-bottom: ${spacing(8)};
  }
`;
const TagList = styled.div`
  margin-bottom: ${spacing(8)};
  display: flex;
  ${mediaMax.tablet} {
    margin-bottom: 0;
  }
`;
const Block = styled.div`
  width: 100%;
  ${mediaMax.tablet} {
    margin-right: ${spacing(24)};
  }
`;
const ServiceTitle = styled.div`
  font-size: ${fontSize(16)};
  font-weight: ${fontWeight('bold')};
  color: ${black};
  margin-bottom: ${spacing(4)};
`;
const ServiceDesc = styled.div`
  font-size: ${fontSize(14)};
  color: ${black};
  margin-bottom: ${spacing(4)};
`;
const ServicePrice = styled.div`
  font-size: ${fontSize(14)};
  font-weight: ${fontWeight('bold')};
  color: ${black};
  margin-bottom: ${spacing(4)};
`;

const MenuContainer = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
`;
const EventTypeLinkContainer = styled.div`
  margin-top: ${spacing(20)};
`;
const AllowIcon = styled(Icon)`
  width: 20px !important;
  height: 20px !important;
`;
const SettingIcon = styled(Icon)`
  color: ${gray01};
`;
const EventTypeLink = styled(Link)`
  color: ${black};
  display: flex;
  align-items: center;
`;
const EventTypeCount = styled.span`
  font-size: ${fontSize(13)};
  line-height: 12px;
`;
